import React from 'react';
import { Link } from 'react-router-dom';

function HomePage() {
  return (
    <div>
      {/* Hero Section */}
      <section className="relative bg-white">
        <div className="flex flex-col lg:flex-row max-w-7xl mx-auto">
          {/* Left Content Section */}
          <div className="relative z-10 px-4 py-10 bg-white sm:px-6 lg:px-8 lg:py-16 lg:w-1/2">
            <div className="mx-auto">
              <h1 className="text-4xl font-extrabold tracking-tight text-gray-900 sm:text-5xl md:text-6xl">
                <span className="block">Reduce food waste,</span>
                <span className="block text-primary-600">fight hunger in Kenya</span>
              </h1>
              <p className="mt-4 text-base text-gray-500 sm:text-lg md:text-xl max-w-md">
                WASTENOT connects food donors with those in need. Join our mission to reduce food waste and address hunger across Kenya.
              </p>
              <div className="mt-6 sm:mt-8 flex flex-col sm:flex-row gap-4">
                <Link
                  to="/register"
                  className="inline-flex items-center justify-center px-8 py-3 border border-transparent text-base font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 md:text-lg"
                >
                  Get Started
                </Link>
                <Link
                  to="/listings"
                  className="inline-flex items-center justify-center px-8 py-3 border border-transparent text-base font-medium rounded-md text-primary-700 bg-primary-100 hover:bg-primary-200 md:text-lg"
                >
                  Browse Listings
                </Link>
              </div>
            </div>
          </div>
          
          {/* Right Image Section */}
          <div className="lg:w-1/2 h-64 sm:h-80 lg:h-auto overflow-hidden">
            <img
              className="w-full h-full object-cover object-center"
              src="/images/food_deistribution.png"
              alt="People sharing food and reducing waste"
            />
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <span className="inline-block px-3 py-1 text-sm font-semibold bg-primary-100 text-primary-800 rounded-full mb-3">HOW IT WORKS</span>
            <h2 className="text-3xl md:text-4xl font-extrabold text-gray-900 mb-4">
              Making food donation simple and efficient
            </h2>
            <p className="mt-4 max-w-2xl text-xl text-gray-600 mx-auto mb-12">
              Our platform connects those with surplus food to those who need it most.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 lg:gap-12">
            {/* Step 1: Donate */}
            <div className="bg-white p-6 rounded-xl shadow-md hover:shadow-lg transition-shadow duration-300 border-t-4 border-primary-500">
              <div className="bg-primary-100 text-primary-700 inline-flex items-center justify-center w-12 h-12 mb-4 rounded-full">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Donate Surplus Food</h3>
              <p className="text-gray-600">
                Hotels, restaurants, and supermarkets can easily post their surplus food through our simple listing process.
              </p>
              <div className="mt-4 pt-4 border-t border-gray-100">
                <Link to="/register" className="text-primary-600 font-medium hover:text-primary-700 flex items-center">
                  Become a donor
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 ml-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                  </svg>
                </Link>
              </div>
            </div>

            {/* Step 2: Find */}
            <div className="bg-white p-6 rounded-xl shadow-md hover:shadow-lg transition-shadow duration-300 border-t-4 border-primary-500">
              <div className="bg-primary-100 text-primary-700 inline-flex items-center justify-center w-12 h-12 mb-4 rounded-full">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Find Available Food</h3>
              <p className="text-gray-600">
                Individuals and organizations in need can easily browse, search and request available donations in their area.
              </p>
              <div className="mt-4 pt-4 border-t border-gray-100">
                <Link to="/listings" className="text-primary-600 font-medium hover:text-primary-700 flex items-center">
                  Browse listings
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 ml-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                  </svg>
                </Link>
              </div>
            </div>

            {/* Step 3: Coordinate */}
            <div className="bg-white p-6 rounded-xl shadow-md hover:shadow-lg transition-shadow duration-300 border-t-4 border-primary-500">
              <div className="bg-primary-100 text-primary-700 inline-flex items-center justify-center w-12 h-12 mb-4 rounded-full">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Coordinate Pickup/Delivery</h3>
              <p className="text-gray-600">
                Schedule convenient pickup times or arrange for delivery to ensure food reaches those who need it most.
              </p>
              <div className="mt-4 pt-4 border-t border-gray-100">
                <Link to="/about" className="text-primary-600 font-medium hover:text-primary-700 flex items-center">
                  Learn more
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 ml-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                  </svg>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-gradient-to-br from-primary-600 to-primary-800 relative overflow-hidden">
        {/* Background patterns */}
        <div className="absolute inset-0 opacity-10">
          <svg className="w-full h-full" viewBox="0 0 800 800" xmlns="http://www.w3.org/2000/svg">
            <circle cx="400" cy="400" r="200" fill="none" stroke="currentColor" strokeWidth="3" />
            <circle cx="500" cy="300" r="100" fill="none" stroke="currentColor" strokeWidth="2" />
            <circle cx="300" cy="500" r="150" fill="none" stroke="currentColor" strokeWidth="2" />
          </svg>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center mb-12">
            <span className="inline-block px-3 py-1 text-sm font-semibold bg-white text-primary-800 rounded-full mb-3">OUR IMPACT</span>
            <h2 className="text-3xl md:text-4xl font-extrabold text-white">
              Making a difference together
            </h2>
          </div>
          
          <div className="grid grid-cols-1 gap-8 md:grid-cols-3 mb-8">
            <div className="bg-white bg-opacity-10 backdrop-blur-sm rounded-xl p-8 text-center transform hover:-translate-y-1 transition-transform duration-300">
              <div className="inline-flex items-center justify-center w-16 h-16 mb-6 rounded-full bg-white bg-opacity-20">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 6l3 1m0 0l-3 9a5.002 5.002 0 006.001 0M6 7l3 9M6 7l6-2m6 2l3-1m-3 1l-3 9a5.002 5.002 0 006.001 0M18 7l3 9m-3-9l-6-2m0-2v2m0 16V5m0 16H9m3 0h3" />
                </svg>
              </div>
              <dt className="text-4xl font-extrabold text-white mb-2">5,000+ kg</dt>
              <dd className="text-xl font-medium text-primary-100">Food Saved</dd>
            </div>
            
            <div className="bg-white bg-opacity-10 backdrop-blur-sm rounded-xl p-8 text-center transform hover:-translate-y-1 transition-transform duration-300">
              <div className="inline-flex items-center justify-center w-16 h-16 mb-6 rounded-full bg-white bg-opacity-20">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                </svg>
              </div>
              <dt className="text-4xl font-extrabold text-white mb-2">1,000+</dt>
              <dd className="text-xl font-medium text-primary-100">People Fed</dd>
            </div>
            
            <div className="bg-white bg-opacity-10 backdrop-blur-sm rounded-xl p-8 text-center transform hover:-translate-y-1 transition-transform duration-300">
              <div className="inline-flex items-center justify-center w-16 h-16 mb-6 rounded-full bg-white bg-opacity-20">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
              </div>
              <dt className="text-4xl font-extrabold text-white mb-2">100+</dt>
              <dd className="text-xl font-medium text-primary-100">Donors & Partners</dd>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-gray-50 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="relative overflow-hidden rounded-2xl bg-white shadow-xl">
            <div className="absolute inset-0">
              <div className="absolute inset-0 bg-gradient-to-r from-primary-500 to-primary-700 mix-blend-multiply opacity-90"></div>
              <img
                className="h-full w-full object-cover"
                src="https://images.unsplash.com/photo-1593113616828-6f22bca04804?ixlib=rb-1.2.1&auto=format&fit=crop&w=1770&q=80"
                alt="People sharing food"
              />
            </div>
            <div className="relative px-6 py-16 sm:px-12 sm:py-20 lg:py-24 lg:px-16">
              <div className="flex flex-col md:flex-row items-center justify-between gap-8">
                <div className="text-center md:text-left">
                  <h2 className="text-3xl font-extrabold tracking-tight text-white sm:text-4xl lg:text-5xl">
                    <span className="block">Ready to make a difference?</span>
                    <span className="block text-primary-100">Join WASTENOT today.</span>
                  </h2>
                  <p className="mt-4 max-w-md mx-auto md:mx-0 text-lg text-primary-50">
                    Become part of our movement to reduce food waste and fight hunger across Kenya.
                  </p>
                </div>
                <div className="flex flex-col sm:flex-row gap-4 mt-8 lg:mt-0">
                  <Link
                    to="/register"
                    className="px-8 py-4 rounded-md text-center shadow-lg bg-white text-primary-700 font-bold hover:bg-primary-50 transition duration-300"
                  >
                    Register Now
                  </Link>
                  <Link
                    to="/about"
                    className="px-8 py-4 rounded-md text-center shadow border-2 border-white text-white font-bold hover:bg-white/10 transition duration-300"
                  >
                    Learn More
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

export default HomePage;
